import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtRM, fmtDate } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Plus, AlertTriangle } from "lucide-react";

export default function Departures() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [deps, setDeps] = useState(null);
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ package_id: "", date: "", return_date: "", total_seats: 45, cost_per_pax: 0, mutawif: "", tour_leader: "" });

  const load = useCallback(() => {
    api.get("/departures").then((r) => setDeps(r.data)).catch(() => toast.error("Failed"));
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const createDep = async (e) => {
    e.preventDefault();
    try {
      await api.post("/departures", { ...form, total_seats: Number(form.total_seats), cost_per_pax: Number(form.cost_per_pax) || null });
      toast.success("Departure created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (!deps) return <div className="flex justify-center h-64 items-center" data-testid="departures-loading"><div className="w-8 h-8 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="departures-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{t("app.departures")}</h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time seat inventory</p>
        </div>
        <Button data-testid="add-departure-button" onClick={() => setOpen(true)} className="bg-brand-800 hover:bg-brand-700 text-sand rounded-full">
          <Plus className="w-4 h-4 mr-1.5" />{t("common.add")}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {deps.map((d) => {
          const pctSold = Math.round((d.sold / d.total_seats) * 100);
          const pctRes = Math.round((d.reserved / d.total_seats) * 100);
          const low = d.available <= 10;
          return (
            <div key={d.departure_id} data-testid={`departure-card-${d.departure_id}`}
              className="pro-card p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-medium text-brand-950 leading-snug">{d.package_name}</h3>
                  <div className="text-xs text-muted-foreground mt-1 font-mono">
                    {fmtDate(d.date)} → {fmtDate(d.return_date)} • {d.flight || "TBA"} • KUL–JED
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-sans text-xl font-bold text-brand-950 tabular-nums">{fmtRM(d.base_price)}</div>
                  {low && d.available > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-destructive" data-testid={`seat-alert-${d.departure_id}`}>
                      <AlertTriangle className="w-3 h-3" />{d.available} left
                    </span>
                  )}
                  {d.available <= 0 && (
                    <span className="text-[10px] font-mono uppercase text-destructive font-semibold">SOLD OUT</span>
                  )}
                </div>
              </div>
              <div className="mt-4">
                <div className="relative h-3 rounded-full bg-sand overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-brand-700" style={{ width: `${pctSold}%` }} />
                  <div className="absolute inset-y-0 bg-gold-500" style={{ left: `${pctSold}%`, width: `${pctRes}%` }} />
                </div>
                <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                  {[["Total", d.total_seats, "text-brand-950"], ["Sold", d.sold, "text-brand-700"], ["Reserved", d.reserved, "text-gold-600"], ["Available", d.available, low ? "text-destructive" : "text-emerald-700"]].map(([l, v, c]) => (
                    <div key={l} className="bg-sand rounded-lg py-2">
                      <div className={`font-sans text-lg font-bold tabular-nums ${c}`} data-testid={`departure-${l.toLowerCase()}-${d.departure_id}`}>{v}</div>
                      <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{l}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-sand-border text-xs text-muted-foreground">
                <span>Mutawif: {d.mutawif || "—"}</span>
                <span>TL: {d.tour_leader || "—"}</span>
              </div>
              <div className="flex items-center justify-between mt-3">
                <div className="text-xs" data-testid={`pnl-line-${d.departure_id}`}>
                  <span className="text-muted-foreground">Rev </span><span className="font-mono text-slate-900 tabular-nums">{fmtRM(d.revenue)}</span>
                  <span className="text-muted-foreground ml-2">Profit </span>
                  <span className={`font-mono tabular-nums ${d.profit >= 0 ? "text-emerald-700" : "text-destructive"}`}>{fmtRM(d.profit)}</span>
                  <span className="text-muted-foreground ml-1">({d.margin}%)</span>
                </div>
                <Button size="sm" variant="outline" data-testid={`manage-departure-${d.departure_id}`}
                  onClick={() => navigate(`/app/departures/${d.departure_id}`)}
                  className="h-7 rounded-full text-xs border-sand-border hover:border-emerald-700/40 hover:text-emerald-800">Manage</Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-departure-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-sans font-semibold text-lg">{t("common.add")} Departure</DialogTitle></DialogHeader>
          <form onSubmit={createDep} className="space-y-4">
            <div>
              <Label>Package</Label>
              <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })} required>
                <SelectTrigger data-testid="departure-package-select"><SelectValue placeholder="Select package" /></SelectTrigger>
                <SelectContent>
                  {packages.map((p) => <SelectItem key={p.package_id} value={p.package_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Departure {t("common.date")}</Label>
                <Input data-testid="departure-date-input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label>Return {t("common.date")}</Label>
                <Input data-testid="departure-return-input" type="date" value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} />
              </div>
              <div>
                <Label>Total Seats</Label>
                <Input data-testid="departure-seats-input" type="number" min="1" value={form.total_seats} onChange={(e) => setForm({ ...form, total_seats: e.target.value })} />
              </div>
              <div>
                <Label>Cost per Pax (RM)</Label>
                <Input data-testid="departure-cost-input" type="number" min="0" value={form.cost_per_pax} onChange={(e) => setForm({ ...form, cost_per_pax: e.target.value })} />
              </div>
              <div>
                <Label>Mutawif</Label>
                <Input data-testid="departure-mutawif-input" value={form.mutawif} onChange={(e) => setForm({ ...form, mutawif: e.target.value })} />
              </div>
            </div>
            <Button data-testid="departure-submit-button" type="submit" className="w-full bg-brand-800 hover:bg-brand-700 text-sand rounded-full">{t("common.save")}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
